import React from 'react';
import { CheckCircle2, Package, AlertTriangle, Clock } from 'lucide-react';

/**
 * LoaderProgressTracker - Shows loading progress across the workflow
 * 
 * Features:
 * - Overall progress bar and statistics
 * - Stack-by-stack progress indicators
 * - Mobile-optimized compact view
 * - Real-time updates as pallets are placed or marked with issues
 */
export default function LoaderProgressTracker({ progress, compact = false }) {
  const {
    currentStackIndex,
    currentPalletInStack,
    totalStacks,
    totalPallets,
    placedCount
  } = progress;

  // Calculate overall progress percentage
  const overallProgress = totalPallets > 0 ? (placedCount / totalPallets) * 100 : 0;

  // Compact mobile view
  if (compact) {
    return (
      <div className="flex items-center space-x-2">
        <div className="relative">
          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
            <span className="text-xs font-medium text-gray-600">
              {Math.round(overallProgress)}%
            </span>
          </div>
          {overallProgress > 0 && (
            <div 
              className="absolute inset-0 rounded-full bg-blue-600 opacity-20"
              style={{
                background: `conic-gradient(#3b82f6 ${overallProgress * 3.6}deg, transparent 0deg)`
              }}
            />
          )}
        </div>
        
        <div className="text-xs">
          <div className="font-medium text-gray-800">
            {placedCount}/{totalPallets}
          </div>
          <div className="text-gray-500">
            Stack {currentStackIndex + 1}/{totalStacks}
          </div>
        </div>
      </div>
    );
  }

  // Full desktop view
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-gray-900">Loading Progress</h3>
        <span className="text-sm text-gray-500">
          {placedCount} of {totalPallets} pallets loaded
        </span>
      </div>

      {/* Overall progress bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Overall Progress</span>
          <span className="font-medium text-gray-900">{Math.round(overallProgress)}%</span>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div 
            className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${overallProgress}%` }}
          />
        </div>
      </div>

      {/* Stack progress */}
      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <Package className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">
            Current Stack: {currentStackIndex + 1} of {totalStacks}
          </span>
        </div>

        {/* Stack indicators */}
        <div className="flex space-x-1">
          {Array.from({ length: totalStacks }, (_, index) => {
            let status = 'pending'; // pending, active, complete
            
            if (index < currentStackIndex) {
              status = 'complete';
            } else if (index === currentStackIndex) {
              status = 'active';
            }

            return (
              <div
                key={index}
                className={`flex-1 h-2 rounded-full transition-colors duration-300 ${
                  status === 'complete' ? 'bg-green-500' :
                  status === 'active' ? 'bg-blue-500' :
                  'bg-gray-200'
                }`}
                title={`Stack ${index + 1} - ${
                  status === 'complete' ? 'Complete' :
                  status === 'active' ? 'In Progress' :
                  'Pending'
                }`}
              />
            );
          })}
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-3 gap-4 pt-2 border-t border-gray-100">
        <div className="text-center">
          <div className="flex items-center justify-center space-x-1 text-green-600 mb-1">
            <CheckCircle2 className="h-3 w-3" />
            <span className="text-xs font-medium">Loaded</span>
          </div>
          <div className="text-lg font-semibold text-gray-900">{placedCount}</div>
        </div>
        
        <div className="text-center">
          <div className="flex items-center justify-center space-x-1 text-blue-600 mb-1">
            <Clock className="h-3 w-3" />
            <span className="text-xs font-medium">Remaining</span>
          </div>
          <div className="text-lg font-semibold text-gray-900">{totalPallets - placedCount}</div>
        </div>
        
        <div className="text-center">
          <div className="flex items-center justify-center space-x-1 text-gray-600 mb-1">
            <Package className="h-3 w-3" />
            <span className="text-xs font-medium">Stacks</span>
          </div>
          <div className="text-lg font-semibold text-gray-900">{totalStacks}</div>
        </div>
      </div>
    </div>
  );
}